import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, filter, firstValueFrom, fromEvent, shareReplay } from 'rxjs';
import { getHiddenIframeRefreshService } from './hiddenIframeRefresh';

export interface RefreshTokenTemplateDTO {
  token: string;
  exp: string;
}

export interface TokenTemplateDTO {
  token: string;
  roles?: string[];
  tenant_id?: string;
  exp?: string;
}

export interface TokensDTO {
  access_token: TokenTemplateDTO;
  refresh_token: RefreshTokenTemplateDTO;
}

export interface DarwinJwtPayload {
  roles?: string[];
  tenant_id?: string;
  exp: number;
  iat: number;
  // Add other JWT payload properties as needed
}

class AuthTokensService {
  private authDomain: string;
  private el: HTMLIFrameElement;
  private iframeLoadEvent$: any;
  private credentials$ = new BehaviorSubject<TokensDTO | null>(null);
  private initialized$ = new BehaviorSubject<boolean>(false);
  private iframePostEvent$: any;
  private rolesFromTokenSubject = new BehaviorSubject<string[] | null>(null);
  private authTenantIdSubject = new BehaviorSubject<string>('');
  private tokenMonitorTimer: NodeJS.Timeout | null = null;
  private darwinLoginUrl: string | null = null;

  constructor(authDomain: string, darwinLoginUrl?: string) {
    this.authDomain = authDomain;
    this.darwinLoginUrl = darwinLoginUrl || null;
    this.el = document.createElement('iframe');

    if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
      console.warn('DEV AUTH BYPASS ACTIVE -- using mock credentials, skipping iframe auth');
      this._injectDevCredentials();
      return;
    }

    this.iframeLoadEvent$ = fromEvent(this.el, 'load').pipe(shareReplay());
    this.iframePostEvent$ = fromEvent<MessageEvent>(window, 'message').pipe(
      filter(
        (event) =>
          event.origin.includes('localhost') ||
          event.origin.includes('darwingov.com')
      )
    );

    this.listenForTokens();
    this.addAuthIframe();
  }

  private _injectDevCredentials(): void {
    const mockCredentials: TokensDTO = {
      access_token: {
        token: 'dev-bypass-mock-token',
        roles: ['redactit_viewer', 'redactit_admin', 'marketplace_viewer'],
        tenant_id: 'dev-tenant-000000000000',
        exp: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      },
      refresh_token: {
        token: 'dev-bypass-mock-refresh-token',
        exp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
    };
    this.credentials$.next(mockCredentials);
    this.rolesFromTokenSubject.next(mockCredentials.access_token.roles || null);
    this.authTenantIdSubject.next(mockCredentials.access_token.tenant_id || '');
    this.initialized$.next(true);
  }

  get rolesFromToken$() {
    return this.rolesFromTokenSubject.asObservable();
  }

  get authTenantId$() {
    return this.authTenantIdSubject.asObservable();
  }

  get credentials() {
    return this.credentials$.asObservable();
  }

  get isInitialized$() {
    return this.initialized$.asObservable();
  }

  async getTokens(): Promise<TokensDTO | null> {
    return firstValueFrom(this.credentials$);
  }

  async getCredentials(): Promise<TokensDTO> {
    return new Promise((resolve) => {
      this.credentials$.subscribe((res) => {
        if (!res) {
          return;
        }
        resolve(res);
      });
    });
  }

  private addAuthIframe() {
    this.iframeLoadEvent$.subscribe();
    const displayDiv = document.body;
    this.el.style.display = 'none';
    this.el.src = this.authDomain;
    displayDiv.appendChild(this.el);
  }

  private listenForTokens() {
    this.iframePostEvent$.subscribe((res: MessageEvent) => {
      let data = res.data;

      // 1. Handle stringified JSON
      if (typeof data === 'string' && (data.startsWith('{') || data.startsWith('['))) {
        try {
          data = JSON.parse(data);
        } catch (e) {
          // Not valid JSON, ignore
          return;
        }
      }

      // 2. Extract credentials (may be wrapped in a 'credentials' field)
      const creds = data?.credentials || (data?.access_token ? data : null);

      // If we didn't find clear credentials but it's an explicit "null" message from identity
      const isNullAuth = data?.credentials === null || data?.access_token === null;

      if (creds?.access_token || isNullAuth) {
        // It's an auth message! 

        if (creds?.access_token) {
          let roles = creds.access_token.roles;
          let tenant_id = creds.access_token.tenant_id;
          const accessToken = typeof creds.access_token === 'string' ? creds.access_token : creds.access_token.token;

          // If roles are missing and we have a JWT, try to decode it
          if (!roles && typeof accessToken === 'string' && accessToken.includes('.')) {
            try {
              const decoded = this.decodeToken(accessToken);
              roles = decoded.roles || null;
              tenant_id = tenant_id || decoded.tenant_id || '';
            } catch (e) {
              console.warn('Failed to decode JWT for roles:', e);
            }
          }

          this.rolesFromTokenSubject.next(roles || null);
          this.authTenantIdSubject.next(tenant_id || '');
          this.credentials$.next(creds);
        } else {
          // Explicitly null
          this.credentials$.next(null);
        }

        // Signal that we have definitively heard from the auth source
        if (!this.initialized$.value) {
          this.initialized$.next(true);
        }
      } else {
        // This is likely noise (webpack, handshake, etc.)
        // We do NOT set initialized$ to true here, we keep waiting for actual tokens or timeout.
        console.debug('AuthTokensService: Received non-auth message, ignoring:', data?.type || 'unknown message');
      }
    });

    // Timeout for initialization if iframe fails or is blocked
    setTimeout(() => {
      if (!this.initialized$.value) {
        console.warn('AuthTokensService: Initialization timeout reached');
        this.initialized$.next(true);
      }
    }, 5000);
  }

  setCredentials(credentials: TokensDTO): Promise<void> {
    return new Promise((resolve) => {
      this.credentials$.next(credentials);

      // Start token monitoring when credentials are set
      if (credentials?.access_token?.exp) {
        this.startTokenMonitoring();
      }

      // Check if iframe is already loaded
      if (this.el.contentWindow) {
        this.el.contentWindow?.postMessage(
          JSON.stringify({ credentials }),
          '*'
        );
        resolve();
      } else {
        this.iframeLoadEvent$.subscribe(() => {
          this.el.contentWindow?.postMessage(
            JSON.stringify({ credentials }),
            '*'
          );
          resolve();
        });
      }
    });
  }

  getRefreshToken(): string | null {
    return this.credentials$.value?.refresh_token?.token || null;
  }

  setAccessToken(token: TokenTemplateDTO): Promise<void> {
    return new Promise((resolve) => {
      const credentials = {
        access_token: token,
        refresh_token: {
          token: this.credentials$.value?.refresh_token?.token || '',
          exp: this.credentials$.value?.refresh_token?.exp || '',
        } as RefreshTokenTemplateDTO,
      };

      setTimeout(async () => {
        await this.setCredentials(credentials);
        return resolve();
      });
    });
  }

  clearTokens(): Promise<void> {
    this.credentials$.next(null);
    this.stopTokenMonitoring(); // Stop monitoring when tokens are cleared

    return new Promise((resolve) => {
      this.el.contentWindow?.postMessage(
        JSON.stringify({ credentials: null }),
        '*'
      );
      resolve();
    });
  }

  async decodedToken() {
    const credentials = await firstValueFrom(this.credentials$);
    return credentials?.access_token;
  }

  decodeToken(token: string) {
    return jwtDecode<DarwinJwtPayload>(token);
  }

  getCurrentCredentials(): TokensDTO | null {
    return this.credentials$.value;
  }

  forceUpdateCredentials(credentials: TokensDTO): void {
    this.credentials$.next(credentials);
    this.rolesFromTokenSubject.next(credentials?.access_token?.roles || null);
    this.authTenantIdSubject.next(credentials?.access_token?.tenant_id || '');
  }

  /**
   * Check if the access token is expiring within the next 5 minutes
   */
  private isTokenExpiring(): boolean {
    const credentials = this.credentials$.value;
    if (!credentials?.access_token?.exp) {
      return false;
    }

    const expTime = new Date(credentials.access_token.exp);
    const now = new Date();
    const timeUntilExpiry = expTime.getTime() - now.getTime();

    // Refresh if expiring within 5 minutes (300000ms)
    return timeUntilExpiry <= 300000 && timeUntilExpiry > 0;
  }

  /**
   * Start monitoring token expiration and refresh proactively
   */
  private startTokenMonitoring(): void {
    this.stopTokenMonitoring(); // Clear any existing timer

    this.tokenMonitorTimer = setInterval(() => {
      if (this.isTokenExpiring()) {
        this.refreshTokenProactively();
      }
    }, 60000); // Check every minute
  }

  /**
   * Stop token monitoring
   */
  private stopTokenMonitoring(): void {
    if (this.tokenMonitorTimer) {
      clearInterval(this.tokenMonitorTimer);
      this.tokenMonitorTimer = null;
    }
  }

  /**
   * Refresh token proactively using hidden iframe service
   */
  private async refreshTokenProactively(): Promise<void> {
    if (!this.darwinLoginUrl) {
      return;
    }

    try {
      const hiddenRefreshService = getHiddenIframeRefreshService(this.authDomain, this.darwinLoginUrl);
      const refreshResult = await hiddenRefreshService.refreshTokens();

      if (refreshResult.success && refreshResult.access_token) {
        // Parse JWT to extract roles and tenant_id
        let roles: string[] = [];
        let tenant_id = '';
        let exp = '';

        try {
          const decodedToken = this.decodeToken(refreshResult.access_token);
          roles = decodedToken.roles || [];
          tenant_id = decodedToken.tenant_id || '';
          exp = new Date(decodedToken.exp * 1000).toISOString();
        } catch {
          // Use existing values as fallback
          const currentCreds = this.credentials$.value;
          roles = currentCreds?.access_token?.roles || [];
          tenant_id = currentCreds?.access_token?.tenant_id || '';
        }

        // Update credentials with new tokens
        const newCredentials = {
          access_token: {
            token: refreshResult.access_token,
            roles: roles,
            tenant_id: tenant_id,
            exp: exp
          },
          refresh_token: {
            token: refreshResult.refresh_token || this.getRefreshToken() || '',
            exp: this.credentials$.value?.refresh_token?.exp || ''
          }
        };

        this.forceUpdateCredentials(newCredentials);
      }
    } catch {
      // Let 401 interceptor handle it as fallback
    }
  }
}

// Singleton instance
let authTokensServiceInstance: AuthTokensService | null = null;

export const createAuthTokensService = (authDomain: string, darwinLoginUrl?: string): AuthTokensService => {
  if (!authTokensServiceInstance) {
    authTokensServiceInstance = new AuthTokensService(authDomain, darwinLoginUrl);
  }
  return authTokensServiceInstance;
};

export const getAuthTokensService = (): AuthTokensService => {
  if (!authTokensServiceInstance) {
    throw new Error('AuthTokensService not initialized. Call createAuthTokensService first.');
  }
  return authTokensServiceInstance;
};